/**
 * hallucinationPrevention.ts — herkennen dat de hallucinatie preventie een opname tegenhield, en
 * dat uitleggen aan een gebruiker.
 *
 * Een transcriptiemodel antwoordt nooit 'niets'. Bij een opname met alleen wind schreef het een
 * volledig boorprofiel uit. De backend (TranscriptionService) houdt zo'n opname tegen en zet de
 * reden in `error_message`. Die meldingen staan daar letterlijk zo; hier worden ze herkend.
 *
 * Eén plek voor de backoffice (projectpagina, /admin/analysis, /admin/validation) en de veldapp,
 * zodat een gebruiker overal dezelfde uitleg leest.
 *
 * Bewust zonder imports: de tests van de backoffice laden dit bestand rechtstreeks in Node.
 */

export type HallucinationPreventionReason = 'no-speech' | 'too-much-text' | 'vocabulary-echo' | 'stock-phrase';

export interface HallucinationPrevention {
  reason: HallucinationPreventionReason;
  /** De melding van de backend zonder 'Transcriptie onbruikbaar: '. */
  detail: string;
}

/** Werd deze opname tegengehouden door de hallucinatie preventie? Anders null. */
export function classifyHallucinationPrevention(message: string | null | undefined): HallucinationPrevention | null {
  const m = /Transcriptie onbruikbaar:\s*([\s\S]*)$/i.exec(String(message || ''));
  if (!m) return null;
  const detail = m[1].trim();
  if (/^geen spraak gehoord/i.test(detail)) return { reason: 'no-speech', detail };
  if (/^het transcript heeft \d+ woorden, maar er werd maar/i.test(detail)) return { reason: 'too-much-text', detail };
  if (/^geen spraak herkend: het transcript is de voorbeeldtekst/i.test(detail)) return { reason: 'vocabulary-echo', detail };
  if (/^geen spraak herkend: .* is een standaardzin/i.test(detail)) return { reason: 'stock-phrase', detail };
  return null;
}

/**
 * De uitleg voor een gebruiker: wat er aan de hand is en wat hij doet. Geen modelnamen of
 * drempels, wel de meting als die er is. Null als de preventie niet de reden was.
 */
export function hallucinationPreventionUserText(message: string | null | undefined): { title: string; text: string } | null {
  const p = classifyHallucinationPrevention(message);
  if (!p) return null;
  const geen = 'Er is geen boorprofiel gemaakt.';
  if (p.reason === 'no-speech') {
    const m = /\(([\d.]+) s spraak in ([\d.]+) s audio\)/.exec(p.detail);
    return {
      title: 'Geen spraak gehoord',
      text: `${m ? `In deze opname van ${Math.round(Number(m[2]))} seconden` : 'In deze opname'} is geen spraak herkend. ${geen}`,
    };
  }
  if (p.reason === 'too-much-text') {
    const m = /heeft (\d+) woorden, maar er werd maar ([\d.]+) s gesproken/.exec(p.detail);
    return {
      title: 'Te weinig spraak',
      text: `${m ? `Er werd maar ${m[2].replace('.', ',')} s gesproken voor ${m[1]} woorden` : 'Er werd te weinig gesproken voor deze tekst'}. ${geen}`,
    };
  }
  return { title: 'Geen spraak gehoord', text: `In deze opname is geen spraak herkend. ${geen}` };
}
